import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CollectionMeta,
  CreateWhatsappTemplateInput,
  SyncWhatsappTemplatesInput,
  SyncWhatsappTemplatesResult,
  WhatsappTemplate,
  WhatsappTemplateListQuery,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['whatsapp-templates'] as const;

export interface TemplatePage {
  items: WhatsappTemplate[];
  meta: CollectionMeta;
}

function toQuery(q: WhatsappTemplateListQuery): Record<string, string | number | undefined> {
  return { page: q.page, pageSize: q.pageSize, channelId: q.channelId, status: q.status };
}

export async function listWhatsappTemplates(
  q: WhatsappTemplateListQuery = {},
): Promise<TemplatePage> {
  const res = await apiClient.getCollection<WhatsappTemplate>('/whatsapp/templates', {
    query: toQuery(q),
  });
  return { items: res.data, meta: res.meta };
}

export const syncWhatsappTemplates = (input: SyncWhatsappTemplatesInput) =>
  apiClient.post<SyncWhatsappTemplatesResult>('/whatsapp/templates/sync', input);

export const createWhatsappTemplate = (input: CreateWhatsappTemplateInput) =>
  apiClient.post<WhatsappTemplate>('/whatsapp/templates', input);

export function useWhatsappTemplates(query: WhatsappTemplateListQuery = {}, enabled = true) {
  return useQuery<TemplatePage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listWhatsappTemplates(query),
    enabled,
  });
}

export function useSyncWhatsappTemplates() {
  const qc = useQueryClient();
  return useMutation<SyncWhatsappTemplatesResult, ApiClientError, SyncWhatsappTemplatesInput>({
    mutationFn: syncWhatsappTemplates,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCreateWhatsappTemplate() {
  const qc = useQueryClient();
  return useMutation<WhatsappTemplate, ApiClientError, CreateWhatsappTemplateInput>({
    mutationFn: createWhatsappTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
